import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dynamic keywords that can be used in templates
const DYNAMIC_KEYWORDS = [
  { keyword: '{{user_name}}', description: 'Full name of the user' },
  { keyword: '{{username}}', description: 'Login username/email of the user' },
  { keyword: '{{password}}', description: 'User password (for activation emails)' },
  { keyword: '{{login_url}}', description: 'URL to the login page' },
  { keyword: '{{organization_name}}', description: 'Organization name' }
];

// Purpose options for templates
const PURPOSE_OPTIONS = [
  { value: 'user_activated', label: 'User Account Activated' },
  { value: 'user_registered', label: 'User Registration Confirmation' },
  { value: 'user_deactivated', label: 'User Account Deactivated' },
  { value: 'password_reset', label: 'Password Reset' },
  { value: 'welcome', label: 'Welcome Message' },
  { value: 'notification', label: 'General Notification' },
  { value: 'other', label: 'Other' }
];

// Quill editor toolbar configuration
const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'align', 'link'
];

const EmailTemplates = () => {
  const { user } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showKeywordHelp, setShowKeywordHelp] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // New template form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    template_key: '',
    trigger_event: 'user_activated',
    subject: '',
    body: '',
    is_enabled: true
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/email-templates?user_id=${user.id}`);
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load email templates');
      }
    }
    setLoading(false);
  }, [user.id]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Generate template key from name
  const generateTemplateKey = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  const handleNewTemplateChange = (field, value) => {
    setNewTemplate(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate template_key from name
      if (field === 'name') {
        updated.template_key = generateTemplateKey(value);
      }
      return updated;
    });
  };

  const handleAddTemplate = () => {
    setView('add');
    setNewTemplate({
      name: '',
      template_key: '',
      trigger_event: 'user_activated',
      subject: '',
      body: '',
      is_enabled: true
    });
    setError('');
    setSuccess('');
    setShowPreview(false);
  };

  const handleEditTemplate = (template) => {
    setView('edit');
    setEditingTemplate({
      ...template,
      _original: { ...template }
    });
    setError('');
    setSuccess('');
    setShowPreview(false);
  };

  const handleCancelEdit = () => {
    setView('list');
    setEditingTemplate(null);
    setNewTemplate({
      name: '',
      template_key: '',
      trigger_event: 'user_activated',
      subject: '',
      body: '',
      is_enabled: true
    });
    setShowPreview(false);
  };

  const handleTemplateChange = (field, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateTemplate = (template) => {
    if (!template.name?.trim()) {
      setError('Template Name is required');
      return false;
    }
    if (!template.subject?.trim()) {
      setError('Email Subject is required');
      return false;
    }
    // Check for body content - strip HTML tags for validation
    const strippedBody = template.body?.replace(/<[^>]*>/g, '').trim();
    if (!strippedBody) {
      setError('Email Body is required');
      return false;
    }
    return true;
  };

  const handleSaveNewTemplate = async () => {
    if (!validateTemplate(newTemplate)) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(
        `${API}/admin/email-templates?user_id=${user.id}`,
        {
          template_key: newTemplate.template_key || generateTemplateKey(newTemplate.name),
          name: newTemplate.name,
          trigger_event: newTemplate.trigger_event,
          subject: newTemplate.subject,
          body: newTemplate.body,
          is_enabled: newTemplate.is_enabled
        }
      );
      
      setSuccess('Template created successfully');
      setView('list');
      setNewTemplate({
        name: '',
        template_key: '',
        trigger_event: 'user_activated',
        subject: '',
        body: '',
        is_enabled: true
      });
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create template');
    }
    setSaving(false);
  };

  const handleSaveTemplate = async () => {
    if (!validateTemplate(editingTemplate)) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(
        `${API}/admin/email-templates/${editingTemplate.id}?user_id=${user.id}`,
        {
          subject: editingTemplate.subject,
          body: editingTemplate.body,
          is_enabled: editingTemplate.is_enabled
        }
      );
      
      setSuccess('Template saved successfully');
      setView('list');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleToggleTemplate = async (template) => {
    try {
      await axios.post(
        `${API}/admin/email-templates/${template.id}/toggle?user_id=${user.id}`
      );
      setSuccess(`Template ${template.is_enabled ? 'disabled' : 'enabled'} successfully`);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update template');
    }
  };

  // Insert keyword into the rich text editor
  const insertKeywordIntoEditor = (keyword, isNew = false) => {
    const currentBody = isNew ? newTemplate.body : editingTemplate?.body || '';
    // Append keyword at the end (or user can manually position cursor)
    const updatedBody = currentBody + keyword;
    
    if (isNew) {
      handleNewTemplateChange('body', updatedBody);
    } else {
      handleTemplateChange('body', updatedBody);
    }
    
    setSuccess(`Inserted ${keyword} - you can move it to the desired position in the editor`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPurposeLabel = (value) => {
    const purpose = PURPOSE_OPTIONS.find(p => p.value === value);
    return purpose ? purpose.label : value;
  };

  // Generate preview with sample data
  const getPreviewHtml = (body) => {
    if (!body) return '';
    
    const sampleData = {
      '{{user_name}}': 'Dr. John Smith',
      '{{username}}': 'john.smith@hospital.com',
      '{{password}}': 'SecurePass123!',
      '{{login_url}}': 'https://dare.exlva.com/login',
      '{{organization_name}}': 'Exlva DARe'
    };
    
    let preview = body;
    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), `<span class="bg-yellow-100 px-1 rounded">${value}</span>`);
    });
    
    return preview;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Template Form Component (used for both Add and Edit)
  const TemplateForm = ({ template, onChange, onSave, onCancel, isNew = false }) => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          {isNew ? 'Add New Template' : `Edit: ${template.name}`}
        </h2>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Template Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Template Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={template.name || ''}
            onChange={(e) => onChange('name', e.target.value)}
            disabled={!isNew}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${!isNew ? 'bg-gray-50' : ''}`}
            placeholder="e.g., Account Activation Email"
          />
          {isNew && template.template_key && (
            <p className="text-xs text-gray-500 mt-1">Key: {template.template_key}</p>
          )}
        </div>

        {/* Purpose */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Purpose / Trigger Event <span className="text-red-500">*</span>
          </label>
          <select
            value={template.trigger_event || 'user_activated'}
            onChange={(e) => onChange('trigger_event', e.target.value)}
            disabled={!isNew}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${!isNew ? 'bg-gray-50' : ''}`}
          >
            {PURPOSE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={template.subject || ''}
            onChange={(e) => onChange('subject', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="e.g., Your account has been activated"
          />
        </div>

        {/* Insert Placeholders */}
        <div className="bg-gray-50 rounded-lg p-4">
          <button
            type="button"
            onClick={() => setShowKeywordHelp(!showKeywordHelp)}
            className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <svg className={`w-4 h-4 mr-2 transition-transform ${showKeywordHelp ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            Insert Dynamic Placeholders (Click to insert at end)
          </button>
          
          {showKeywordHelp && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DYNAMIC_KEYWORDS.map((kw) => (
                <button
                  key={kw.keyword}
                  type="button"
                  onClick={() => insertKeywordIntoEditor(kw.keyword, isNew)}
                  className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-sky-50 hover:border-sky-300 transition-colors text-left"
                >
                  <code className="text-sky-600 font-mono text-sm">{kw.keyword}</code>
                  <span className="text-gray-500 text-xs ml-2">{kw.description}</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            💡 Tip: Click a placeholder to insert it. You can then cut/paste it to the desired location in the editor.
          </p>
        </div>

        {/* Rich Text Editor for Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Email Body <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-sm text-sky-600 hover:text-sky-800 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
          
          {/* Quill Editor */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <ReactQuill
              theme="snow"
              value={template.body || ''}
              onChange={(value) => onChange('body', value)}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Compose your email content here. Use the toolbar for formatting..."
              style={{ minHeight: '300px' }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Use the toolbar above to format text. Placeholders like {'{{user_name}}'} will be replaced with actual values when sent.
          </p>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">📧 Email Preview</span>
              <span className="text-xs text-gray-500">Placeholders shown with sample values</span>
            </div>
            <div className="p-4 bg-white">
              <div className="mb-3 pb-3 border-b border-gray-100">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Subject:</span> {template.subject || '(No subject)'}
                </p>
              </div>
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: getPreviewHtml(template.body) || '<p class="text-gray-400 italic">No content yet...</p>' }}
              />
            </div>
          </div>
        )}

        {/* Enabled Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="text-sm font-medium text-gray-700">Template Enabled</span>
            <p className="text-xs text-gray-500">When enabled, this template will be used for sending emails</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={template.is_enabled}
              onChange={(e) => onChange('is_enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {isNew ? 'Create Template' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto">
      {/* Custom styles for Quill editor */}
      <style>{`
        .ql-container {
          min-height: 250px;
          font-size: 14px;
        }
        .ql-editor {
          min-height: 250px;
        }
        .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background: #f9fafb;
        }
        .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
      `}</style>

      {/* Breadcrumb */}
      <nav className="mb-4">
        <ol className="flex items-center space-x-2 text-sm">
          <li><span className="text-gray-500">Settings</span></li>
          <li>
            <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li>
            {view === 'list' ? (
              <span className="text-red-600 font-medium">Email Templates</span>
            ) : (
              <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                Email Templates
              </button>
            )}
          </li>
          {view !== 'list' && (
            <>
              <li>
                <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className="text-red-600 font-medium">
                  {view === 'add' ? 'Add Template' : 'Edit Template'}
                </span>
              </li>
            </>
          )}
        </ol>
      </nav>

      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
          <p className="mt-2 text-gray-600">Manage email templates for automated system notifications</p>
        </div>
        {view === 'list' && (
          <button
            onClick={handleAddTemplate}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Template
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Content based on view */}
      {view === 'list' && (
        <>
          {/* Templates List */}
          {templates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No email templates yet</h3>
              <p className="mt-2 text-gray-500">
                Create your first email template to start sending automated notifications.
              </p>
              <button
                onClick={handleAddTemplate}
                className="mt-6 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Template
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purpose
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-sky-100 rounded-lg">
                            <svg className="h-5 w-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{template.name}</div>
                            <div className="text-xs text-gray-500">{template.template_key}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{getPurposeLabel(template.trigger_event)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleTemplate(template)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            template.is_enabled
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {template.is_enabled ? (
                            <>
                              <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></span>
                              Enabled
                            </>
                          ) : (
                            <>
                              <span className="w-2 h-2 bg-gray-400 rounded-full mr-1.5"></span>
                              Disabled
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(template.updated_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-sky-600 hover:text-sky-900 mr-4"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info Card */}
          <div className="mt-8 bg-sky-50 border border-sky-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-sky-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-sky-800">
                <h4 className="font-medium mb-1">How Email Templates Work</h4>
                <ul className="list-disc list-inside space-y-1 text-sky-700">
                  <li>Templates define the content of automated emails sent by the system</li>
                  <li>Use the rich text editor to format your emails with bold, italic, lists, and links</li>
                  <li>Use dynamic keywords like <code className="bg-sky-100 px-1 rounded">{'{{user_name}}'}</code> that get replaced with actual values</li>
                  <li>Preview your email before saving to see how placeholders will be replaced</li>
                  <li>Disabled templates will skip email sending for their associated events</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {view === 'add' && (
        <TemplateForm
          template={newTemplate}
          onChange={handleNewTemplateChange}
          onSave={handleSaveNewTemplate}
          onCancel={handleCancelEdit}
          isNew={true}
        />
      )}

      {view === 'edit' && editingTemplate && (
        <TemplateForm
          template={editingTemplate}
          onChange={handleTemplateChange}
          onSave={handleSaveTemplate}
          onCancel={handleCancelEdit}
          isNew={false}
        />
      )}
    </div>
  );
};

export default EmailTemplates;
